import { i18n } from "@lingui/core";
import type { ToastApi } from "@mobsuccess-devops/react-ui/Toaster";

import axios from "axios";

import { isAuthFailure } from "../public/auth/auth-failure";

// The MutationCache onError hook in root.tsx runs outside React, where no
// toaster context is reachable, so the toast api is handed over through this
// module-level slot by <MutationErrorToaster/> once the <Toaster> is mounted.
// Errors raised before that (or after unmount) are dropped silently — with no
// UI mounted, there is nowhere to show them.
let toastApi: Pick<ToastApi, "error"> | null = null;

export function registerMutationErrorToaster(
  api: Pick<ToastApi, "error">,
): () => void {
  toastApi = api;
  return () => {
    if (toastApi === api) {
      toastApi = null;
    }
  };
}

export function notifyMutationError(error: unknown): void {
  if (isAuthFailure(error)) {
    // catch-401 and the session query own the auth pipeline; a toast on top
    // of the re-authentication flow would only add noise.
    return;
  }
  toastApi?.error({
    title: i18n._("mutation-error.toast.title"),
    description:
      getServerMessage(error) ?? i18n._("mutation-error.toast.generic"),
  });
}

const MAX_SERVER_MESSAGE_LENGTH = 200;

// Backends answer a rejected request with a human-readable reason in one of a
// few conventional fields; anything longer than a sentence or shaped like an
// HTML error page is not meant for the user and falls back to the generic
// wording.
function getServerMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) {
    return null;
  }
  const data: unknown = error.response?.data;
  const candidates: unknown[] = [data];
  if (typeof data === "object" && data !== null) {
    for (const [field, value] of Object.entries(data)) {
      if (field === "message" || field === "detail" || field === "error") {
        candidates.push(value);
      }
    }
  }
  for (const candidate of candidates) {
    if (
      typeof candidate === "string" &&
      candidate.length > 0 &&
      candidate.length <= MAX_SERVER_MESSAGE_LENGTH &&
      !candidate.trimStart().startsWith("<")
    ) {
      return candidate;
    }
  }
  return null;
}
